import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CategoryIcon from '@mui/icons-material/Category';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SummarizeIcon from '@mui/icons-material/Summarize';
import HistoryIcon from '@mui/icons-material/History';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const reports = [
  {
    title: 'Debt Repayment by Payment Date',
    description: 'View schedule based on payment due dates.',
    icon: <CalendarMonthIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/debt-repayment-by-payment-date',
  },
  {
    title: 'Debt Repayment by Bank',
    description: 'Group repayment schedule by bank.',
    icon: <AccountBalanceIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/debt-repayment-by-bank',
  },
  {
    title: 'Debt Repayment by Interest/Principal',
    description: 'Split schedule view by interest and principal.',
    icon: <CallSplitIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/debt-repayment-by-split',
  },
  {
    title: 'Debt Repayment by Loan Type',
    description: 'Organize schedule based on loan types.',
    icon: <CategoryIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/debt-repayment-by-loan-type',
  },
  {
    title: 'Availability Period Report',
    description: 'Track loan availability periods.',
    icon: <EventAvailableIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/availability-period',
  },
  {
    title: 'Headroom Analysis Report',
    description: 'Analyze financial headroom.',
    icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/headroom-analysis',
  },
  {
    title: 'Bank Term Report (Detailed)',
    description: 'Detailed view of bank terms.',
    icon: <ListAltIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/bank-term-detail',
  },
  {
    title: 'Bank Term Report (Summary)',
    description: 'Summary overview of bank terms.',
    icon: <SummarizeIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/bank-term-summary',
  },
  {
    title: 'Audit Trail Report',
    description: 'Track changes and activities.',
    icon: <HistoryIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/reports/audit-trail',
  },
];

const ReportSelection: React.FC = () => {
  return (
    <Box sx={{ p: 3, bgcolor: '#F9FAFB', flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#111827', fontWeight: 'bold' }}>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Select a report to view, filter, and export data.
      </Typography>
      <Grid container spacing={3}>
        {reports.map((report, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                transition: 'box-shadow 0.3s ease-in-out',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                '&:hover': {
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                },
                bgcolor: '#FFFFFF'
              }}
            >
              <CardActionArea
                component={RouterLink}
                to={report.path}
                sx={{
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <ListItemIcon sx={{ mr: 2, minWidth: 'auto' }}>
                    {report.icon}
                  </ListItemIcon>
                  <Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#111827', fontSize: '1rem' }}>
                      {report.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                      {report.description}
                    </Typography>
                  </Box>
                </Box>
                <ChevronRightIcon sx={{ color: '#9CA3AF' }} />
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ReportSelection; 